// Four questions an admin actually asks about a user: are they still using the product, and when
// did they last do each of the three things that matter — add a field, make a soil map, run an Rx.

/* * The four activity timestamps, all ISO strings from the admin read. Absent = never, or not yet tracked. */
export interface UserActivity {
  last_contact_at?: string | null;
  last_field_added_at?: string | null;
  last_soil_map_at?: string | null;
  last_rx_at?: string | null;
}

/*
 *
 * "3d ago" / "2h ago" / "just now", or `null` when there is nothing to say.
 *
 * `null` for absent is the point: an admin reading a blank cell learns "never" or "not tracked", whereas a
 * zero or an epoch date reads as a real event at a real time. The distinction matters most for exactly the
 * users this panel exists to find — the ones who have done nothing.
 */
export function agoText(
  iso: string | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const secs = Math.round((now - t) / 1000);
  if (secs < 0) return "just now"; // clock skew — never render a future age
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

/* * Absolute timestamp for the cell's tooltip — the relative text is scannable, this is checkable. */
export function exactText(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleString() : undefined;
}

/* * How stale is this user? Drives the emphasis, not the value. */
export function staleness(
  iso: string | null | undefined,
  now: number = Date.now(),
): "none" | "recent" | "old" | "never" {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "never";
  const days = (now - t) / 86_400_000;
  if (days < 7) return "recent";
  if (days < 60) return "none";
  return "old";
}
